import preuseTests from "../preuse_tests.json";
import {
  decodePreUseErrorCodePayload,
  getExpiratoryCalibrationScalarFromRow,
  isExpiratoryFlowSensorCalibrationRow,
} from "../utils/preuseErrorDecode";

/** @typedef {{ bit_position: number, troubleshoot: string, action: string, service_action: string }} CatalogErrorRow */
/** @typedef {{ test_id: number, test_name: string, test_description: string, error_code_width: number, errors: CatalogErrorRow[] }} CatalogTestRow */

/** @type {CatalogTestRow[]} */
const CATALOG = preuseTests;

const byTestId = new Map(CATALOG.map((row) => [row.test_id, row]));

/**
 * Borrow `errors` from another test when this entry has none (shared bit layout).
 * Key = test_id with empty/missing rules, value = donor test_id from the same JSON.
 */
const CATALOG_ERRORS_FALLBACK = {
  113029: 113026,
};

/**
 * @param {CatalogTestRow} def
 * @returns {CatalogTestRow}
 */
function withResolvedErrors(def) {
  if (Array.isArray(def.errors) && def.errors.length > 0) {
    return def;
  }
  const donorId = CATALOG_ERRORS_FALLBACK[def.test_id];
  if (donorId == null) return def;
  const donor = byTestId.get(donorId);
  if (!donor?.errors?.length) return def;
  return {
    ...def,
    errors: donor.errors,
    error_code_width: Number(def.error_code_width) || Number(donor.error_code_width) || 16,
  };
}

/** @param {number} widthBits */
function maskForWidth(widthBits) {
  if (widthBits >= 32) return 0xffffffff >>> 0;
  if (widthBits <= 0) return 0;
  return (((1 << widthBits) >>> 0) - 1) >>> 0;
}

function formatFullHex(code) {
  return (Number(code) >>> 0).toString(16).toUpperCase().padStart(8, "0");
}

/** Bit set for bit_position within masked error code */
function isBitSet(maskedUnsigned, bitPosition) {
  if (bitPosition < 0 || bitPosition >= 32) return false;
  return ((maskedUnsigned >>> bitPosition) & 1) === 1;
}

/**
 * @param {CatalogTestRow} def
 * @param {number} errorCodeRaw
 */
export function matchCatalogErrors(def, errorCodeRaw) {
  const width = Number(def.error_code_width) || 16;
  const mask = maskForWidth(width);
  const masked = (Number(errorCodeRaw) >>> 0) & mask;

  const matched = def.errors.filter((e) => isBitSet(masked, e.bit_position));
  const unmatched = def.errors.filter((e) => !isBitSet(masked, e.bit_position));

  matched.sort((a, b) => a.bit_position - b.bit_position);
  unmatched.sort((a, b) => a.bit_position - b.bit_position);

  return { masked, matched, unmatched };
}

/**
 * @param {CatalogErrorRow[]} ascendingErrors
 */
function resolveRollingServiceAction(ascendingErrors) {
  let last = "";
  for (const row of ascendingErrors) {
    const s = row.service_action?.trim?.() ?? "";
    if (s && s !== "Same as above") last = row.service_action;
  }
  return last;
}

/**
 * @param {CatalogErrorRow[]} matchedSorted
 */
function buildTroubleshootSteps(matchedSorted) {
  const steps = [];
  for (const e of matchedSorted) {
    steps.push(`[Bit ${e.bit_position}] ${e.troubleshoot}`);
    const a = e.action?.trim?.() ?? "";
    if (a && a !== "Same as above") {
      steps.push(`→ Action: ${a}`);
    }
  }
  return steps.length ? steps : ["No catalogue entry matches the set bits in this error code."];
}

/** @param {CatalogErrorRow[]} defSortedAsc */
function inheritedActionForBit(defSortedAsc, bitPosition) {
  const idx = defSortedAsc.findIndex((d) => d.bit_position === bitPosition);
  if (idx < 0) return "";
  const direct = defSortedAsc[idx].action?.trim?.() ?? "";
  if (direct && direct !== "Same as above") return direct;
  for (let i = idx - 1; i >= 0; i--) {
    const a = defSortedAsc[i].action?.trim?.() ?? "";
    if (a && a !== "Same as above") return a;
  }
  return "";
}

/** @param {CatalogErrorRow[]} defSortedAsc */
function inheritedServiceForBit(defSortedAsc, bitPosition) {
  const idx = defSortedAsc.findIndex((d) => d.bit_position === bitPosition);
  if (idx < 0) return "";
  const direct = defSortedAsc[idx].service_action?.trim?.() ?? "";
  if (direct && direct !== "Same as above") return direct;
  for (let i = idx - 1; i >= 0; i--) {
    const s = defSortedAsc[i].service_action?.trim?.() ?? "";
    if (s && s !== "Same as above") return defSortedAsc[i].service_action;
  }
  return "";
}

/** One UI card per raised bit — actions inherit “Same as above” from lower bits */
function buildFaultCards(matchedSorted, defSortedAsc, testDescription, fallbackService) {
  if (!matchedSorted.length) return [];
  return matchedSorted.map((entry) => {
    const steps = [];
    steps.push(entry.troubleshoot);
    const act = inheritedActionForBit(defSortedAsc, entry.bit_position);
    if (act) steps.push(`→ Action: ${act}`);

    let svc =
      inheritedServiceForBit(defSortedAsc, entry.bit_position) ||
      fallbackService ||
      "";

    return {
      bitId: `B${entry.bit_position}`,
      title: entry.troubleshoot,
      subtitle: testDescription || entry.test_description || "",
      troubleshootSteps: steps,
      serviceAction:
        svc || "Contact service with this error code and machine serial / UDI.",
    };
  });
}

/** @param {number} testId */
export function getTestDefinition(testId) {
  return byTestId.get(testId);
}

/** @param {number} testId */
export function getCatalogTestName(testId) {
  return byTestId.get(testId)?.test_name;
}

/** @param {object} row */
function firstSensorValue(row) {
  if (isExpiratoryFlowSensorCalibrationRow(row)) {
    return String(getExpiratoryCalibrationScalarFromRow(row));
  }
  if (row.raw_data !== undefined) {
    if (Array.isArray(row.raw_data)) {
      const arr = row.raw_data;
      if (arr.length === 0) return "—";
      if (arr.length <= 12) return arr.join(", ");
      return `${arr.length} samples · ${arr.slice(0, 6).join(", ")}, …`;
    }
    if (typeof row.raw_data === "number") return String(row.raw_data);
    return String(row.raw_data);
  }
  const v = row.data?.[0]?.data;
  return v !== undefined && v !== null ? String(v) : "—";
}

/**
 * @param {object} row Decoded decode row ({ testid, errorcode, raw_data, … })
 */
export function getFailureDetailFromCatalog(row) {
  const tid = Number(row.testid);
  const nid = row.name_id != null ? Number(row.name_id) : null;
  const def =
    (Number.isFinite(tid) ? getTestDefinition(tid) : null) ??
    (nid != null && Number.isFinite(nid) ? getTestDefinition(nid) : null);

  if (!def) {
    return null;
  }

  const defEffective = withResolvedErrors(def);

  const catalogPayload = decodePreUseErrorCodePayload(row, {
    test_id: defEffective.test_id,
    test_name: defEffective.test_name,
    test_description: defEffective.test_description,
    errors: defEffective.errors,
    error_code_width: defEffective.error_code_width,
  });

  const masked = catalogPayload.masked_error_code >>> 0;
  const defsSortedAsc = [...defEffective.errors].sort((a, b) => a.bit_position - b.bit_position);

  const matched = catalogPayload.decoded_errors.map((e) => ({
    bit_position: e.bit_position,
    troubleshoot: e.troubleshoot,
    action: e.action,
    service_action: e.service_action,
    test_description: e.test_description,
  }));

  const errorHex = formatFullHex(masked);
  const showErrorCodeBadge = masked !== 0;

  const troubleshootSteps =
    matched.length > 0
      ? buildTroubleshootSteps(matched)
      : masked !== 0
        ? [
            `No catalogue rules matched this error bitmap for ${defEffective.test_name} (masked 0x${masked.toString(
              16
            )}). Error field: ${catalogPayload.error_code} · decoded via ${catalogPayload.decode_source}`,
          ]
        : [
            catalogPayload.decode_source === "compound"
              ? `Zero error bitmap after parse. DATA=${catalogPayload.data ?? "n/a"}, ERROR_HEX=${catalogPayload.error_code}`
              : `Fault recorded with zero bitmask. Parsed error: 0x${formatFullHex(row.errorcode)}`,
          ];

  let rollupServiceAction = "";
  if (matched.length) {
    rollupServiceAction =
      resolveRollingServiceAction(matched) || resolveRollingServiceAction(defsSortedAsc);
  } else if (masked !== 0) {
    rollupServiceAction = resolveRollingServiceAction(defsSortedAsc);
  }

  const matchedSorted = [...matched].sort((a, b) => a.bit_position - b.bit_position);

  const faultCards = buildFaultCards(
    matchedSorted,
    defsSortedAsc,
    defEffective.test_description,
    rollupServiceAction
  );

  const primaryProbeTitle =
    matched.length > 0 ? matched[0].troubleshoot : defEffective.test_name;

  const primaryCheckId =
    matchedSorted.length > 1
      ? matchedSorted.map((m) => `B${m.bit_position}`).join("+")
      : matchedSorted.length === 1
        ? `B${matchedSorted[0].bit_position}`
        : "—";

  /** Only raised bits appear as cards (see `faultCards`). */
  const relatedChecks = [];

  const sensorFriendly = `${defEffective.test_name} Value`;
  let sensorLine = `${sensorFriendly.replace(/\s+/g, " ")} : ${firstSensorValue(row)}`;
  if (catalogPayload.data_description && !isExpiratoryFlowSensorCalibrationRow(row)) {
    sensorLine += ` · ${catalogPayload.data_description}`;
  }

  return {
    moduleTitle: defEffective.test_name.toUpperCase(),
    categorySubtitle: defEffective.test_description,
    sensorLine,
    troubleshootSteps,
    serviceAction:
      rollupServiceAction || "Contact service with this error code and machine serial / UDI.",
    faultCards,
    primaryCheckId,
    primaryProbeTitle,
    relatedChecks,
    testName: defEffective.test_name,
    errorHex,
    showErrorCodeBadge,
  };
}
